import {
  UpsellPageBanner,
  UpsellPageProductSelection,
  UpsellPageTimer,
} from "@redotech/redo-conversion-core-common/upsell-page/upsell-page-db-validator";
import {
  BannerStatus,
  TextAppearance,
  TextSize,
  UpsellPageElementType,
  UpsellPageLayoutType,
  UpsellPageProductSource,
} from "@redotech/redo-conversion-core-common/upsell-page/upsell-page-types";
import { DiscountValueType } from "@redotech/redo-discounts-common/discount";
import { ProductInfo } from "@redotech/shopify-storefront/products";
import { groupInput, input, InputSchema } from "@redotech/ui/form";
import { DeepPartial } from "@redotech/util/type";

export const upsellPageProductSelectionDefault: UpsellProductSelectionInProgress =
  {
    upsellProductSource: UpsellPageProductSource.MANUAL,
    showIfAlreadyInCart: false,
    type: UpsellPageElementType.UPSELL_PRODUCT,
    discountValueType: DiscountValueType.PERCENTAGE,
    discountAmount: "10",
  };

export type UpsellProductSelectionInProgress =
  DeepPartial<UpsellPageProductSelection> & {
    upsellProductSource: UpsellPageProductSource;
    type: UpsellPageElementType.UPSELL_PRODUCT;
  };

export const upsellPageForm = groupInput({
  name: input<string | undefined>(),
  layout: groupInput({
    type: input<UpsellPageLayoutType>(),
    timer: input<UpsellPageTimer>(),
    banner: input<UpsellPageBanner>(),
    upsellProductsSelections: input<UpsellProductSelectionInProgress[]>(),
    excludedProductTags: input<string[]>(),
  }),
});

export type UpsellPageValue = InputSchema.Value<typeof upsellPageForm>;
export type UpsellPageForm = InputSchema.Input<typeof upsellPageForm>;

export function getUpsellPageDefault(): UpsellPageValue {
  return {
    name: undefined,
    layout: {
      type: UpsellPageLayoutType.ONE_PRODUCT_UPSELL,
      timer: {
        type: UpsellPageElementType.TIMER,
        showTimer: true,
        backgroundColor: "#ffffff",
        totalTime: "5",
        timerBannerStatus: BannerStatus.INFO,
        timerTextSize: TextSize.MEDIUM,
        timerTextColor: TextAppearance.SUCCESS,
        supportingTextSize: TextSize.SMALL,
        supportingText: "Offer will expire when the time runs out",
      },
      banner: {
        type: UpsellPageElementType.BANNER,
        showBanner: true,
        title: "Before you go!",
        titleSize: TextSize.MEDIUM,
        supportingText:
          "Your order is confirmed, but you can still add the following for a limited time",
        supportingTextSize: TextSize.SMALL,
        backgroundColor: "#ffffff",
      },
      upsellProductsSelections: [upsellPageProductSelectionDefault],
      excludedProductTags: [],
    },
  };
}

export function transformToShopifyProduct(
  productInfo: ProductInfo,
): ShopifyProduct {
  const transformedProduct: ShopifyProduct = {
    ...productInfo,
    image: productInfo.images.nodes[0]
      ? {
          src: productInfo.images.nodes[0].url,
          altText: productInfo.images.nodes[0].altText ?? null,
        }
      : { src: null, altText: null },
    body_html: undefined,
    images: productInfo.images.nodes.map((image: any) => ({
      src: image.url,
      altText: image.altText ?? null,
    })),
    variants: productInfo.variants.nodes.map((variant: any) => ({
      admin_graphql_api_id: variant.id,
      sku: variant.sku ?? undefined,
      inventory_policy: undefined,
      inventory_quantity: undefined,
      inventory_tracked: undefined,
      requires_shipping: undefined,
      ...variant,
      price: variant.price.amount,
      compareAtPrice: variant.compareAtPrice?.amount ?? null,
      image: variant.image
        ? { src: variant.image.url ?? null, altText: null }
        : { src: null, altText: null },
    })),
  };
  return transformedProduct;
}

export type StaticUpsellPageElement =
  | UpsellPageBanner
  | UpsellPageTimer
  | UpsellProductSelectionInProgress;

export interface ShopifyProduct {
  id: string;
  title: string;
  description: string;
  handle: string;
  image: { src: string | null; altText: string | null };
  images: { src: string | null; altText: string | null }[];
  body_html?: string;
  tags: string[];
  variants: ShopifyVariant[];
}

export interface ShopifyVariant {
  admin_graphql_api_id: string;
  inventory_policy?: string;
  inventory_quantity?: number;
  inventory_tracked?: boolean;
  sku?: string | null;
  title: string;
  requires_shipping?: boolean;
  price: string;
  compareAtPrice?: string | null;
  image: { src: string | null; altText: string | null };
  productTitle?: string;
}
